import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { closeDialog, openDialog } from '../../../../../app/dialog/dialog.actions';
import { IPersonalNotesWidgetProps } from './types';
import { NotesList } from './NotesList';
import { NoteEditor } from './NoteEditor';
import { useDebounce } from '../../../../../utils/debounce.utils';
import { useSelect } from '../../../../../utils/selector.utils';
import { personalNotesPageSelectors } from '../../../../../app/page/page-storage/personal-notes/personal-notes.selectors';
import { personalNoteSelectors } from '../../../../../app/resource/personal-note/personal-note.selectors';
import {
  updatePersonalNote,
  deletePersonalNote,
  storePersonalNotes,
} from '../../../../../app/page/page-storage/personal-notes/personal-notes.actions';
import { IPersonalNote } from '../../../../../app/resource/personal-note/personal-note.types';

interface IDraftNote extends Pick<IPersonalNote, 'title' | 'content'> {
  title: string;
  content: string;
}

export const PersonalNotesWidget: FunctionComponent<IPersonalNotesWidgetProps> = ({ isNotesListCollapsed = false }) => {
  const dispatch = useDispatch();

  const noteIds = useSelect(personalNotesPageSelectors.selectPersonalNotesIds);
  const allNotes = useSelect(personalNoteSelectors.selectResourceList);

  // Filter and sort notes by the ids in the page state
  const notes = noteIds
    .map((id) => allNotes.find((note) => note.id === id))
    .filter((note): note is IPersonalNote => note !== undefined);

  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);

  const selectedNote = notes.find((note) => note.id === selectedNoteId) || null;

  const [draft, setDraft] = useState<IDraftNote>({ title: '', content: '' });

  // Auto-select first note when notes are loaded and set draft from it
  useEffect(() => {
    if (notes.length > 0 && !selectedNoteId) {
      const first = notes[0];
      setSelectedNoteId(first.id);
      setDraft({ title: first.title, content: first.content });
    }
  }, [notes, selectedNoteId]);

  const handleSelectNote = (noteId: string) => {
    setSelectedNoteId(noteId);
    const note = notes.find((n) => n.id === noteId);
    setDraft({ title: note?.title || '', content: note?.content || '' });
  };

  const handleReorderNotes = useCallback(
    (movedNoteId: IPersonalNote['id'], nextIds: IPersonalNote['id'][]) => {
      dispatch(updatePersonalNote(movedNoteId, { sort: nextIds.indexOf(movedNoteId) }));
      dispatch(storePersonalNotes(nextIds));
    },
    [dispatch],
  );

  const saveDraftNow = (noteId: string, payload: IDraftNote) => {
    dispatch(updatePersonalNote(noteId, payload));
  };

  const saveDraftDebounce = useDebounce(
    (noteId: string, payload: IDraftNote) => {
      dispatch(updatePersonalNote(noteId, payload));
    },
    2000,
    [dispatch],
  );

  // Flush pending debounced save when switching note or unmounting so last draft is not lost
  useEffect(() => {
    return () => {
      saveDraftDebounce.flush();
    };
  }, [selectedNoteId, saveDraftDebounce]);

  const handleDraftChange = (field: 'title' | 'content') => (value: string) => {
    setDraft((prev) => {
      const next = { ...prev, [field]: value };
      if (selectedNoteId) {
        saveDraftDebounce(selectedNoteId, next);
      }
      return next;
    });
  };

  const handleDraftBlur = (field: 'title' | 'content') => (value: string) => {
    setDraft((prev) => {
      const next = { ...prev, [field]: value };
      if (selectedNoteId) {
        saveDraftNow(selectedNoteId, next);
        saveDraftDebounce.cancel();
      }
      return next;
    });
  };

  const handleDeleteNote = () => {
    if (!selectedNoteId) {
      return;
    }
    const noteIdToDelete = selectedNoteId;

    dispatch(
      openDialog('confirmation', {
        title: 'Are you sure you want to delete this note?',
        message: 'This action cannot be undone.',
        onConfirm: () => {
          dispatch(closeDialog('confirmation'));
          saveDraftDebounce.cancel();
          dispatch(deletePersonalNote(noteIdToDelete));
          const remainingNotes = notes.filter((n) => n.id !== noteIdToDelete);
          if (remainingNotes.length > 0) {
            handleSelectNote(remainingNotes[0].id);
          } else {
            setSelectedNoteId(null);
            setDraft({ title: '', content: '' });
          }
        },
        onDeny: () => dispatch(closeDialog('confirmation')),
        styles: { zIndex: '999999999', position: 'fixed' },
      }),
    );
  };

  return (
    <div className="personal-notes-container">
      <NotesList
        notes={notes}
        selectedNoteId={selectedNoteId}
        onSelectNote={handleSelectNote}
        onReorder={handleReorderNotes}
        isCollapsed={isNotesListCollapsed}
      />
      <NoteEditor
        note={selectedNote}
        title={draft.title}
        content={draft.content}
        onContentChange={handleDraftChange('content')}
        onTitleChange={handleDraftChange('title')}
        onContentBlur={handleDraftBlur('content')}
        onTitleBlur={handleDraftBlur('title')}
        onDeleteNote={handleDeleteNote}
      />
    </div>
  );
};
